import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowLeftIcon, UserIcon, PhoneIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import useLocalStorage from '../../utils/useLocalStorage';
import { FORMA_LINK_BTN } from '../../utils/listPageUi';

const TrainerForm = ({ isEditing = false, initialData = null }) => {
  const navigate = useNavigate();
  const { addItem: addTrainer, updateItem: updateTrainer } = useLocalStorage('trainers');

  const [formData, setFormData] = useState({
    trainerCode: initialData?.trainerCode || `FORM-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
    name: initialData?.name || '',
    phone: initialData?.phone || '',
    email: initialData?.email || '',
    specialty: initialData?.specialty || '',
    status: initialData?.status || 'active',
    joinDate: initialData?.joinDate || new Date().toISOString().split('T')[0],
    photo: null
  });

  const [photoPreview, setPhotoPreview] = useState(initialData?.photo || null);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setErrors(prev => ({ ...prev, photo: 'La photo ne peut pas dépasser 5MB' }));
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        photo: file,
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    if (!formData.phone.trim()) newErrors.phone = 'Le téléphone est requis';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    try {
      if (isEditing && initialData) {
        // Mettre à jour un formateur existant
        const updatedTrainer = {
          ...initialData,
          ...formData,
          photo: photoPreview // On garde l'URL de l'image au lieu du File object
        };
        const result = updateTrainer(updatedTrainer);
        console.log('Formateur mis à jour avec succès:', result);
      } else {
        // Ajouter un nouveau formateur
        const newTrainer = {
          ...formData,
          photo: photoPreview // On garde l'URL de l'image au lieu du File object
        };
        const result = addTrainer(newTrainer);
        console.log('Formateur ajouté avec succès:', result);
      }
      
      navigate('/trainers');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du formateur:', error);
      alert('Une erreur est survenue lors de l\'enregistrement des données');
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <Link to="/trainers" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Retour à la liste
        </Link>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
          {isEditing ? 'Modifier le formateur' : 'Ajouter un formateur'}
        </h2>
        
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Photo upload */}
            <div className="col-span-full">
              <div className="flex flex-col items-center space-y-4">
                <div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-forma-500 to-forma-700 ring-4 ring-forma-100 dark:ring-forma-900/60">
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserIcon className="h-16 w-16 text-slate-900 dark:text-slate-50" />
                  )}
                </div>
                
                <div className="w-full max-w-xs">
                  <label className="block w-full">
                    <span className="sr-only">Choisir une photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="block w-full text-sm text-slate-600 dark:text-slate-400 file:mr-4 file:rounded-lg file:border-0 file:bg-forma-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white file:transition-colors hover:file:bg-forma-500"
                    />
                  </label>
                  {errors.photo && (
                    <p className="mt-1 text-sm text-red-400">{errors.photo}</p>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    PNG, JPG jusqu'à 5MB
                  </p>
                </div>
              </div>
            </div>
            
            {/* Nom */}
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Nom complet*
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-3 h-5 w-5 text-slate-500 dark:text-slate-400" />
                <input 
                  type="text"
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-800/80 border ${
                    errors.name ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'
                  } rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-forma-500 focus:border-forma-500`}
                  required
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-400">{errors.name}</p>
              )}
            </div>
            
            {/* Code */}
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Code formateur
              </label>
              <input 
                type="text"
                name="trainerCode" 
                value={formData.trainerCode}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-forma-500 focus:border-forma-500"
                readOnly
              />
            </div>
            
            {/* Téléphone */}
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Téléphone*
              </label>
              <div className="relative">
                <PhoneIcon className="absolute left-3 top-3 h-5 w-5 text-slate-500 dark:text-slate-400" />
                <input 
                  type="text"
                  name="phone" 
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-800/80 border ${
                    errors.phone ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'
                  } rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-forma-500 focus:border-forma-500`}
                  required
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-400">{errors.phone}</p>
              )}
            </div>
            
            {/* Email */}
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Email
              </label>
              <input 
                type="email"
                name="email" 
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/80 border ${
                  errors.email ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'
                } rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-forma-500 focus:border-forma-500`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email}</p>
              )}
            </div>
            
            {/* Spécialité */}
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Spécialité
              </label>
              <div className="relative">
                <AcademicCapIcon className="absolute left-3 top-3 h-5 w-5 text-slate-500 dark:text-slate-400" />
                <input 
                  type="text"
                  name="specialty" 
                  value={formData.specialty}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-forma-500 focus:border-forma-500"
                />
              </div>
            </div>
            
            {/* Statut */}
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Statut
              </label>
              <select 
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-forma-500 focus:border-forma-500"
              >
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
                <option value="on_leave">En congé</option>
              </select>
            </div>
            
            {/* Date d'engagement */}
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                Date d'engagement
              </label>
              <input 
                type="date"
                name="joinDate" 
                value={formData.joinDate}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-forma-500 focus:border-forma-500"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-4 mt-8">
            <Link 
              to="/trainers"
              className="px-6 py-3 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              Annuler
            </Link>
            <button
              type="submit"
              className={clsx(FORMA_LINK_BTN, 'px-6 py-3')}
            >
              {isEditing ? 'Mettre à jour' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrainerForm;